import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Save, Eye, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
    blogApi,
    BLOG_CATEGORIES,
    renderMarkdownPreview,
} from "@/lib/blogApi";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const emptyPost = () => ({
    title: "",
    slug: "",
    meta_title: "",
    meta_description: "",
    category: "Life",
    tags: [],
    hero_image_url: "",
    hero_image_alt: "",
    body_markdown: "",
    faq: [],
    disclaimer: "",
    publish_at: "",
});

function Field({ label, help, children, required }) {
    return (
        <label className="block">
            <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-mm-muted">
                    {label} {required && <span className="text-rose-600">*</span>}
                </span>
            </div>
            {children}
            {help && <p className="mt-1 text-[0.72rem] text-mm-muted">{help}</p>}
        </label>
    );
}

export default function AdminBlogEditor() {
    const { id } = useParams();
    const isNew = !id || id === "new";
    const navigate = useNavigate();
    const [form, setForm] = useState(emptyPost());
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [tagInput, setTagInput] = useState("");
    const [showPreview, setShowPreview] = useState(false);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        blogApi
            .get(id)
            .then((p) => {
                setForm({
                    title: p.title || "",
                    slug: p.slug || "",
                    meta_title: p.meta_title || "",
                    meta_description: p.meta_description || "",
                    category: p.category || "Life",
                    tags: p.tags || [],
                    hero_image_url: p.hero_image_url || "",
                    hero_image_alt: p.hero_image_alt || "",
                    body_markdown: p.body_markdown || "",
                    faq: p.faq || [],
                    disclaimer: p.disclaimer || "",
                    publish_at: p.publish_at || "",
                });
            })
            .catch(() => toast.error("Could not load post"))
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const update = (patch) => setForm((f) => ({ ...f, ...patch }));

    const addTag = () => {
        const v = tagInput.trim();
        if (!v) return;
        if (form.tags.includes(v)) return;
        update({ tags: [...form.tags, v] });
        setTagInput("");
    };

    const removeTag = (t) => update({ tags: form.tags.filter((x) => x !== t) });

    const addFaq = () => update({ faq: [...form.faq, { q: "", a: "" }] });
    const updateFaq = (i, patch) => {
        const next = [...form.faq];
        next[i] = { ...next[i], ...patch };
        update({ faq: next });
    };
    const removeFaq = (i) => update({ faq: form.faq.filter((_, idx) => idx !== i) });

    const submit = async () => {
        if (!form.title || !form.body_markdown || !form.category) {
            toast.error("Title, category, and body are required");
            return;
        }
        if (!form.hero_image_url || !form.hero_image_alt) {
            toast.error("Hero image URL and alt text are required");
            return;
        }
        if (!form.meta_title || !form.meta_description) {
            toast.error("Meta title and meta description are required");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                slug: form.slug?.trim() || null,
                publish_at: form.publish_at?.trim() || null,
                faq: form.faq.filter((x) => x.q && x.a),
            };
            const result = isNew
                ? await blogApi.create(payload)
                : await blogApi.update(id, payload);
            toast.success(isNew ? "Draft created" : "Saved");
            navigate(`/admin/blog/${result.id}/edit`, { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail?.errors) {
                toast.error(detail.errors.join(", "));
            } else {
                toast.error(detail || err?.message || "Save failed");
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center gap-2 p-10 text-sm text-mm-muted">
                <Loader2 className="animate-spin" size={16} /> Loading post...
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-blog-editor">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <Link
                        to="/admin/blog"
                        className="inline-flex items-center gap-1 text-xs text-mm-muted hover:text-mm-blue"
                        data-testid="editor-back"
                    >
                        <ArrowLeft size={12} /> Back to posts
                    </Link>
                    <h1 className="mt-1 font-serif text-3xl text-mm-navy">
                        {isNew ? "New blog post" : "Edit post"}
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowPreview((v) => !v)}
                        className="mm-btn-outline !px-4 !py-2 text-sm"
                        data-testid="editor-preview-toggle"
                    >
                        <Eye size={14} />
                        {showPreview ? "Hide preview" : "Show preview"}
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving}
                        className="mm-btn-primary !px-4 !py-2 text-sm"
                        data-testid="editor-save"
                    >
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                        Save draft
                    </button>
                </div>
            </div>

            <div className={`grid gap-6 ${showPreview ? "lg:grid-cols-2" : ""}`}>
                <div className="space-y-5 rounded-xl border border-mm-line bg-white p-5">
                    <Field label="Title" required>
                        <Input
                            value={form.title}
                            onChange={(e) => update({ title: e.target.value })}
                            data-testid="editor-field-title"
                        />
                    </Field>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Slug" help="Leave empty to auto-generate from title.">
                            <Input
                                value={form.slug}
                                onChange={(e) => update({ slug: e.target.value })}
                                placeholder="auto-generated"
                                data-testid="editor-field-slug"
                            />
                        </Field>
                        <Field label="Category" required>
                            <Select value={form.category} onValueChange={(v) => update({ category: v })}>
                                <SelectTrigger data-testid="editor-field-category">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {BLOG_CATEGORIES.map((c) => (
                                        <SelectItem key={c} value={c}>
                                            {c}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>
                    <Field label="Meta title" required help="Used as the <title> and OG title.">
                        <Input
                            value={form.meta_title}
                            onChange={(e) => update({ meta_title: e.target.value })}
                            data-testid="editor-field-meta-title"
                        />
                    </Field>
                    <Field label="Meta description" required help="140–180 characters recommended.">
                        <textarea
                            value={form.meta_description}
                            onChange={(e) => update({ meta_description: e.target.value })}
                            className="h-20 w-full rounded-lg border border-mm-line bg-mm-bg/40 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-mm-sky"
                            data-testid="editor-field-meta-desc"
                        />
                    </Field>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Hero image URL" required>
                            <Input
                                value={form.hero_image_url}
                                onChange={(e) => update({ hero_image_url: e.target.value })}
                                data-testid="editor-field-hero-url"
                            />
                        </Field>
                        <Field label="Hero image alt text" required>
                            <Input
                                value={form.hero_image_alt}
                                onChange={(e) => update({ hero_image_alt: e.target.value })}
                                data-testid="editor-field-hero-alt"
                            />
                        </Field>
                    </div>
                    <Field label="Tags" help="Enter and press Add. Freeform strings.">
                        <div className="flex items-center gap-2">
                            <Input
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addTag();
                                    }
                                }}
                                placeholder="e.g. term, florida"
                                data-testid="editor-field-tag-input"
                            />
                            <button
                                onClick={addTag}
                                className="rounded-full border border-mm-line px-3 py-1 text-xs font-medium hover:bg-mm-bg"
                                data-testid="editor-tag-add"
                            >
                                Add
                            </button>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1">
                            {form.tags.map((t) => (
                                <span
                                    key={t}
                                    className="inline-flex items-center gap-1 rounded-full bg-mm-bg px-2.5 py-0.5 text-xs text-mm-ink"
                                    data-testid={`editor-tag-${t}`}
                                >
                                    {t}
                                    <button onClick={() => removeTag(t)} aria-label={`Remove ${t}`}>
                                        <X size={11} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </Field>
                    <Field
                        label="Body (Markdown)"
                        required
                        help="H2 headings become the TOC. Supports tables, bold, italic, links, blockquotes, lists."
                    >
                        <textarea
                            value={form.body_markdown}
                            onChange={(e) => update({ body_markdown: e.target.value })}
                            className="h-80 w-full rounded-lg border border-mm-line bg-mm-bg/40 p-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-mm-sky"
                            data-testid="editor-field-body"
                        />
                    </Field>
                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <span className="text-xs font-semibold uppercase tracking-wider text-mm-muted">
                                FAQ (optional)
                            </span>
                            <button
                                onClick={addFaq}
                                className="inline-flex items-center gap-1 rounded-full border border-mm-line px-3 py-1 text-xs font-medium hover:bg-mm-bg"
                                data-testid="editor-faq-add"
                            >
                                <Plus size={12} /> Add FAQ
                            </button>
                        </div>
                        <div className="space-y-2">
                            {form.faq.map((item, i) => (
                                <div
                                    key={i}
                                    className="rounded-lg border border-mm-line p-3"
                                    data-testid={`editor-faq-row-${i}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <Input
                                            value={item.q}
                                            onChange={(e) => updateFaq(i, { q: e.target.value })}
                                            placeholder="Question"
                                            data-testid={`editor-faq-q-${i}`}
                                        />
                                        <button
                                            onClick={() => removeFaq(i)}
                                            className="rounded-full p-1 text-rose-600 hover:bg-rose-50"
                                            data-testid={`editor-faq-remove-${i}`}
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                    <textarea
                                        value={item.a}
                                        onChange={(e) => updateFaq(i, { a: e.target.value })}
                                        placeholder="Answer"
                                        className="mt-2 h-20 w-full rounded-lg border border-mm-line bg-mm-bg/40 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-mm-sky"
                                        data-testid={`editor-faq-a-${i}`}
                                    />
                                </div>
                            ))}
                            {form.faq.length === 0 && (
                                <div className="rounded-lg border border-dashed border-mm-line p-3 text-center text-xs text-mm-muted">
                                    No FAQ items. Click <strong>Add FAQ</strong> to include a FAQPage schema.
                                </div>
                            )}
                        </div>
                    </div>
                    <Field label="Disclaimer (optional)" help="Falls back to the default disclaimer in Blog settings.">
                        <textarea
                            value={form.disclaimer}
                            onChange={(e) => update({ disclaimer: e.target.value })}
                            className="h-20 w-full rounded-lg border border-mm-line bg-mm-bg/40 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-mm-sky"
                            data-testid="editor-field-disclaimer"
                        />
                    </Field>
                    <Field
                        label="Publish at (UTC, optional)"
                        help="Format: 2026-10-20T13:00:00Z. Approve to schedule; the 15-min job promotes to published."
                    >
                        <Input
                            value={form.publish_at || ""}
                            onChange={(e) => update({ publish_at: e.target.value })}
                            placeholder="2026-10-20T13:00:00Z"
                            data-testid="editor-field-publish-at"
                        />
                    </Field>
                </div>

                {showPreview && (
                    <div
                        className="rounded-xl border border-mm-line bg-white p-5"
                        data-testid="editor-preview"
                    >
                        <div className="mb-4 border-b border-mm-line pb-3">
                            <div className="text-[0.72rem] uppercase tracking-wider text-mm-blue">
                                {form.category}
                            </div>
                            <h1 className="mt-1 font-serif text-2xl text-mm-navy">
                                {form.title || "Untitled"}
                            </h1>
                            {form.hero_image_url && (
                                <img
                                    src={form.hero_image_url}
                                    alt={form.hero_image_alt}
                                    className="mt-3 h-48 w-full rounded-lg object-cover"
                                />
                            )}
                        </div>
                        <div
                            className="prose-sm max-w-none text-sm text-mm-ink [&_h2]:mt-5 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-mm-navy [&_p]:my-2 [&_a]:text-mm-blue [&_a]:underline [&_strong]:font-semibold [&_blockquote]:border-l-4 [&_blockquote]:border-mm-sky [&_blockquote]:pl-3 [&_blockquote]:text-mm-muted [&_ul]:pl-5 [&_ul]:list-disc [&_ol]:pl-5 [&_ol]:list-decimal [&_code]:rounded [&_code]:bg-mm-bg [&_code]:px-1"
                            dangerouslySetInnerHTML={{
                                __html: renderMarkdownPreview(form.body_markdown),
                            }}
                        />
                        {form.faq.length > 0 && (
                            <div className="mt-6 border-t border-mm-line pt-4">
                                <h3 className="mb-3 font-serif text-lg text-mm-navy">
                                    Frequently asked questions
                                </h3>
                                <div className="space-y-2">
                                    {form.faq.map((item, i) => (
                                        <div
                                            key={i}
                                            className="rounded-lg border border-mm-line p-3 text-sm"
                                        >
                                            <strong className="text-mm-navy">{item.q}</strong>
                                            <p className="mt-1 text-mm-ink">{item.a}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
